import { useState, useMemo } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { PageLoader, EmptyState } from "@/components/PageStates"
import { Search, X, Check } from "lucide-react"
import { cn } from "@/lib/utils"

const EMOJI_LIBRARY = {
  Smileys: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","😚","😙","🥲","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🫢","🫣","🤫","🤔","🫡","🤐","🤨","😐","😑","😶","🫥","😏","😒","🙄","😬","🤥","😌","😔","😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🥵","🥶","🥴","😵","🤯","🤠","🥳","🥸","😎","🤓","🧐"],
  People: ["👋","🤚","🖐️","✋","🖖","🫱","🫲","🫳","🫴","👌","🤌","🤏","✌️","🤞","🫰","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","🫵","👍","👎","✊","👊","🤛","🤜","👏","🙌","🫶","👐","🤲","🤝","🙏","💪","🦾","🦿","🦵","🦶","👂","🦻","👃","🧠","🫀","🫁","🦷","🦴","👀","👁️","👅","👄"],
  Animals: ["🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐻‍❄️","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🙈","🙉","🙊","🐒","🐔","🐧","🐦","🐤","🐣","🐥","🦆","🦅","🦉","🦇","🐺","🐗","🐴","🦄","🐝","🪱","🐛","🦋","🐌","🐞","🐜","🪲","🪳","🦟","🦗","🕷️","🦂","🐢","🐍","🦎","🦖","🦕","🐙","🦑"],
  Food: ["🍏","🍎","🍐","🍊","🍋","🍌","🍉","🍇","🍓","🫐","🍈","🍒","🍑","🥭","🍍","🥥","🥝","🍅","🍆","🥑","🥦","🥬","🥒","🌶️","🫑","🌽","🥕","🫒","🧄","🧅","🥔","🍠","🫘","🥐","🍞","🥖","🥨","🧀","🥚","🍳","🧈","🥞","🧇","🥓","🥩","🍗","🍖","🌭","🍔","🍟","🍕","🫓","🥪","🥙","🧆","🌮","🌯","🫔","🥗"],
  Travel: ["✈️","🚗","🚕","🚙","🚌","🚎","🏎️","🚓","🚑","🚒","🚐","🛻","🚚","🚛","🚜","🏍️","🛵","🚲","🛴","🛹","🛼","🚁","✈️","🛩️","🚀","🛸","🚢","⛵","🛶","🗾","🌍","🌎","🌏","🗺️","🧭","🏔️","⛰️","🌋","🗻","🏕️","🏖️","🏜️","🏝️","🏞️","🏟️","🏛️","🏗️","🧱","🪨","🪵"],
  Objects: ["⌚","📱","📲","💻","⌨️","🖥️","🖨️","🖱️","🖲️","💾","💿","📀","📼","📷","📸","📹","🎥","📽️","🎞️","📞","☎️","📟","📠","📺","📻","🎙️","🎚️","🎛️","🧭","⏱️","⏲️","⏰","🕰️","📡","🔋","🔌","💡","🔦","🕯️","🪔","🧯","🛢️","💸","💵","💴","💶","💷","🪙","💰","💳","🧾"],
  Symbols: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💟","☮️","✝️","☪️","🕉️","☸️","✡️","🔯","🕎","☯️","☦️","🛐","⛎","♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓","🆔","⚛️","🉑","☢️","☣️","📴","📳","🈶","🈚","🈸","🈺","🈷️"],
  Flags: ["🏁","🚩","🎌","🏴","🏳️","🏳️‍🌈","🏳️‍⚧️","🏴‍☠️","🇦🇫","🇦🇱","🇩🇿","🇦🇸","🇦🇩","🇦🇴","🇦🇮","🇦🇶","🇦🇬","🇦🇷","🇦🇲","🇦🇼","🇦🇺","🇦🇹","🇦🇿","🇧🇸","🇧🇭","🇧🇩","🇧🇧","🇧🇾","🇧🇪","🇧🇿","🇧🇯","🇧🇲","🇧🇹","🇧🇴","🇧🇦","🇧🇼","🇧🇷","🇮🇴","🇻🇬","🇧🇳","🇧🇬","🇧🇫","🇧🇮","🇰🇭","🇨🇲","🇨🇦"],
}

const CATEGORIES = Object.keys(EMOJI_LIBRARY)

export default function EmojisPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [activeCategory, setActiveCategory] = useState("Smileys")
  const [selectedEmoji, setSelectedEmoji] = useState(null)

  const { data: savedEmojis, isLoading } = useQuery({
    queryKey: ["ui-emojis"],
    queryFn: () => api.get("/ui/emojis"),
  })

  const saveMutation = useMutation({
    mutationFn: (data) =>
      fetch("/api/ui/emojis", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(data),
      }).then((r) => r.json()),
    onSuccess: () => queryClient.invalidateQueries(["ui-emojis"]),
  })

  const filteredEmojis = useMemo(() => {
    const emojis = EMOJI_LIBRARY[activeCategory] || []
    if (!search) return emojis
    return Object.values(EMOJI_LIBRARY).flat().filter((e) => e.includes(search))
  }, [activeCategory, search])

  const savedMap = useMemo(() => {
    const map = {}
    for (const e of savedEmojis?.data || []) {
      map[e.emoji] = e
    }
    return map
  }, [savedEmojis])

  if (isLoading) return <PageLoader />

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--text-primary))]">Emoji Library</h1>
        <p className="text-sm text-[hsl(var(--text-secondary))]">Select emojis for bot messages</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
                  <Input
                    placeholder="Search emojis..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { setActiveCategory(cat); setSearch("") }}
                    className={cn(
                      "px-3 py-1 rounded-md text-xs font-medium transition-colors",
                      activeCategory === cat && !search
                        ? "bg-[hsl(var(--brand-500))] text-white"
                        : "bg-[hsl(var(--bg-hover))] text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]"
                    )}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-1">
                {filteredEmojis.map((emoji, i) => (
                  <button
                    key={`${emoji}-${i}`}
                    onClick={() => setSelectedEmoji(emoji)}
                    className={cn(
                      "relative h-10 w-10 flex items-center justify-center rounded-lg text-xl transition-all hover:bg-[hsl(var(--bg-hover))] hover:scale-110",
                      selectedEmoji === emoji && "bg-[hsl(var(--brand-500))]/10 ring-2 ring-[hsl(var(--brand-500))]",
                      savedMap[emoji] && "ring-1 ring-[hsl(var(--status-success))]"
                    )}
                    title={savedMap[emoji]?.name || emoji}
                  >
                    {emoji}
                    {savedMap[emoji] && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[hsl(var(--status-success))] flex items-center justify-center">
                        <Check className="h-2 w-2 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {filteredEmojis.length === 0 && (
                <p className="text-center text-[hsl(var(--text-muted))] py-8">No emojis found</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Selected Emoji</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedEmoji ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">{selectedEmoji}</span>
                    <div>
                      <p className="text-xs text-[hsl(var(--text-muted))]">Unicode</p>
                      <p className="text-sm font-mono text-[hsl(var(--text-primary))]">
                        {selectedEmoji.codePointAt(0).toString(16)}
                      </p>
                    </div>
                  </div>
                  {savedMap[selectedEmoji] ? (
                    <div className="text-sm text-[hsl(var(--text-secondary))]">
                      <p>Saved as: <strong>{savedMap[selectedEmoji].name}</strong></p>
                      <p>Category: {savedMap[selectedEmoji].category}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        placeholder="Name (e.g. smile)"
                        id="emoji-name"
                        defaultValue={""}
                      />
                      <Input
                        placeholder="Category (e.g. smileys)"
                        id="emoji-category"
                        defaultValue={activeCategory.toLowerCase()}
                      />
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          const name = document.getElementById("emoji-name")?.value
                          const category = document.getElementById("emoji-category")?.value
                          if (name) {
                            saveMutation.mutate({ name, emoji: selectedEmoji, category })
                          }
                        }}
                      >
                        Save Emoji
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-[hsl(var(--text-muted))] text-center py-8">
                  Click an emoji to select it
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Saved Emojis ({savedEmojis?.data?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-auto">
                {(savedEmojis?.data || []).map((e) => (
                  <div key={e.name} className="flex items-center gap-2 text-sm">
                    <span className="text-lg">{e.emoji}</span>
                    <span className="text-[hsl(var(--text-secondary))]">{e.name}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">{e.category}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
